import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import "./StudentPaperAssignmentCalendar.css";
import secret from "../../../secret.json";
import { retrieveCommentaryPDFAPI } from "../../assignment/assignmentAPI";

const normalizeDate = (value) => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDateKey = (value) => {
  const date = normalizeDate(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatFullDate = (date) =>
  date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });

const formatDateTime = (value) =>
  new Date(value).toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const getContentIcon = (type) => {
  if (type === "lesson") return "📖";
  if (type === "exam") return "📋";
  if (type === "homework") return "📌";
  return "📌";
};

const getContentLabel = (type) => {
  if (type === "lesson") return "수업";
  if (type === "exam") return "시험";
  if (type === "homework") return "과제";
  return "기록";
};

const parseClassContent = (classContent) => {
  if (Array.isArray(classContent)) {
    return classContent.filter((item) => item && (item.description || item.file_url || item.file_name || item.s3_key));
  }

  if (typeof classContent === "string") {
    try {
      const parsed = JSON.parse(classContent);
      return Array.isArray(parsed)
        ? parsed.filter((item) => item && (item.description || item.file_url || item.file_name || item.s3_key))
        : [];
    } catch (error) {
      console.warn("Failed to parse class content", error);
    }
  }

  return [];
};

// Helper: Check if homework is overdue
const isHomeworkOverdue = (endDate) => {
  if (!endDate) return false;
  return new Date(endDate) < new Date();
};

// Helper: Format deadline display
const formatDeadline = (endDate) => {
  if (!endDate) return null;
  return new Date(endDate).toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// 알림장 카테고리 감지 함수 (Student version - with string parsing)
const detectDayCategories = (assignment) => {
  const categories = new Set();

  // 온라인 과제인 경우 (type 속성이 있고 paper_assignment, class_content가 아니면)
  if (assignment.type && assignment.type !== 'paper_assignment' && assignment.type !== 'class_content') {
    categories.add(assignment.type);
    return Array.from(categories);
  }

  // class_content 검사
  let classContent = assignment.class_content;

  // class_content가 문자열인 경우 JSON 파싱
  if (typeof classContent === "string") {
    try {
      classContent = JSON.parse(classContent);
    } catch (error) {
      console.warn("Failed to parse class_content:", error);
      classContent = [];
    }
  }

  if (Array.isArray(classContent)) {
    classContent.forEach((content) => {
      if (content?.type === "lesson") categories.add("lesson");
      if (content?.type === "exam") categories.add("exam");
      if (content?.type === "homework") categories.add("homework");
    });
  }

  // homework_files 검사
  if (Array.isArray(assignment.homework_files) && assignment.homework_files.length > 0) {
    categories.add("homework_files");

    // 채점완료 검사
    const hasGraded = assignment.homework_files.some(
      (file) => file.student_submission?.score !== null &&
                file.student_submission?.score !== undefined
    );
    if (hasGraded) {
      categories.add("graded");
    }
  }

  // 온라인 과제 타입 검사 (Student의 경우 assignment.type)
  if (assignment.type) {
    categories.add(assignment.type);
  }

  // 카테고리가 없으면 "general" 추가
  if (categories.size === 0) {
    categories.add("general");
  }

  return Array.from(categories);
};

const getCategoryLabel = (category) => {
  const labels = {
    lesson: "수업",
    homework: "현장과제",
    exam: "시험",
    homework_files: "지필과제",
    graded: "채점완료",
    analysis: "지문분석",
    comprehensive: "내용이해",
    paragraph: "모의고사",
    vocabulary: "단어",
    sentence: "문장",
    grammar: "어법",
    general: "코멘트"
  };
  return labels[category] || category;
};

const StudentPaperAssignmentCalendar = ({
  assignments = [],
  onlineAssignments = [],
  onDownloadHomework = () => {},
}) => {
  const { student } = useSelector((state) => state.student);
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedAssignmentType, setSelectedAssignmentType] = useState("all");

  const normalizedAssignments = useMemo(() => {
    return assignments.filter((assignment) => {
      if (!assignment?.created_at) return false;

      // Assignment type filter - if online type is selected, exclude paper assignments
      if (selectedAssignmentType !== "all" && selectedAssignmentType !== "paper") {
        return false;
      }

      return true;
    });
  }, [assignments, selectedAssignmentType]);

  const assignmentsByDate = useMemo(() => {
    return normalizedAssignments.reduce((acc, assignment) => {
      // Use class_date if available, otherwise fall back to created_at
      const displayDate = assignment.class_date || assignment.created_at;
      const key = getDateKey(displayDate);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(assignment);
      return acc;
    }, {});
  }, [normalizedAssignments]);

  // 온라인 과제를 날짜별로 그룹핑 (paper_assignment, class_content 타입 제외, 필터 적용)
  const onlineAssignmentsByDate = useMemo(() => {
    return onlineAssignments.reduce((acc, assignment) => {
      const assignmentDate = assignment?.finished_at || assignment?.created_at;
      if (!assignmentDate) return acc;
      // paper_assignment와 class_content 타입은 이미 알림장으로 표시되므로 제외
      if (assignment.type === 'paper_assignment' || assignment.type === 'class_content') return acc;

      // Assignment type filter (only for online assignments)
      if (selectedAssignmentType !== "all" && selectedAssignmentType !== "paper") {
        if (assignment.type !== selectedAssignmentType) {
          return acc;
        }
      } else if (selectedAssignmentType === "paper") {
        // If "paper" is selected, exclude online assignments
        return acc;
      }

      const key = getDateKey(assignmentDate);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(assignment);
      return acc;
    }, {});
  }, [onlineAssignments, selectedAssignmentType]);

  useEffect(() => {
    if (!selectedDay) {
      return;
    }

    const key = getDateKey(selectedDay);
    const hasPaperAssignments = assignmentsByDate[key] && assignmentsByDate[key].length > 0;
    const hasOnlineAssignments = onlineAssignmentsByDate[key] && onlineAssignmentsByDate[key].length > 0;

    // Only reset selectedDay if there are no assignments at all (neither paper nor online)
    if (!hasPaperAssignments && !hasOnlineAssignments) {
      setSelectedDay(null);
    }
  }, [assignmentsByDate, onlineAssignmentsByDate, selectedDay]);

  const datesWithAssignments = useMemo(
    () =>
      Object.keys(assignmentsByDate).map((key) => {
        const [year, month, day] = key.split("-").map(Number);
        return new Date(year, month - 1, day);
      }),
    [assignmentsByDate]
  );

  const selectedDayKey = selectedDay ? getDateKey(selectedDay) : null;

  const selectedAssignments = useMemo(() => {
    if (!selectedDayKey) {
      return [];
    }

    const paperAssignments = assignmentsByDate[selectedDayKey] ?? [];
    const onlineRecords = onlineAssignmentsByDate[selectedDayKey] ?? [];

    // Combine and mark type
    const combinedAssignments = [
      ...paperAssignments.map(a => ({ ...a, _recordType: 'paper' })),
      ...onlineRecords.map(a => ({ ...a, _recordType: 'online' }))
    ];

    return combinedAssignments.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [assignmentsByDate, onlineAssignmentsByDate, selectedDayKey]);

  useEffect(() => {
    if (!activeAssignment) {
      return;
    }

    const stillExists = selectedAssignments.some(
      (assignment) => assignment.id === activeAssignment.id
    );

    if (!stillExists) {
      setActiveAssignment(null);
    }
  }, [selectedAssignments, activeAssignment]);

  const modalClassContent = useMemo(() => {
    if (!activeAssignment) {
      return [];
    }
    return parseClassContent(activeAssignment.class_content);
  }, [activeAssignment]);

  // 날짜별 카테고리 계산 (알림장 + 온라인 과제)
  const categoriesByDate = useMemo(() => {
    const result = {};

    // 알림장 카테고리 추가
    Object.keys(assignmentsByDate).forEach((dateKey) => {
      const assignmentsForDay = assignmentsByDate[dateKey];
      const categoriesSet = new Set();

      assignmentsForDay.forEach((assignment) => {
        const categories = detectDayCategories(assignment);
        categories.forEach((cat) => categoriesSet.add(cat));
      });

      result[dateKey] = Array.from(categoriesSet);
    });

    // 온라인 과제 카테고리 추가
    Object.keys(onlineAssignmentsByDate).forEach((dateKey) => {
      const assignments = onlineAssignmentsByDate[dateKey];
      if (!result[dateKey]) {
        result[dateKey] = [];
      }

      const categoriesSet = new Set(result[dateKey]);

      assignments.forEach((assignment) => {
        // Online assignments have type directly on the assignment object
        const type = assignment.type;
        // Skip paper_assignment and class_content types as those are already in paper assignments
        if (type && type !== 'paper_assignment' && type !== 'class_content') {
          categoriesSet.add(type);
        }
      });

      result[dateKey] = Array.from(categoriesSet);
    });

    return result;
  }, [assignmentsByDate, onlineAssignmentsByDate]);

  // Generate calendar days for the current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Add empty slots for days before the month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  }, [currentMonth]);

  const changeMonth = (offset) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  const handleDayClick = (day) => {
    if (!day) {
      return;
    }

    const normalizedDay = normalizeDate(day);
    const key = getDateKey(normalizedDay);

    if (selectedDay && getDateKey(selectedDay) === key) {
      setSelectedDay(null);
      return;
    }

    setSelectedDay(normalizedDay);
  };

  const showCommentaryPDF = async (assignmentId, studentId) => {
    try {
      const pdfWindow = window.open();
      const response = await retrieveCommentaryPDFAPI(assignmentId, studentId);
      if (response.status !== 200) {
        alert("해설지를 불러오는데 실패했습니다.");
        pdfWindow.close();
        return;
      }
      const file = new Blob([response.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      pdfWindow.location.href = fileURL;
    } catch (error) {
      console.error("Error loading commentary PDF:", error);
      alert("해설지를 불러오는데 실패했습니다.");
    }
  };

  const totalAssignmentsCount = normalizedAssignments.length;
  const selectedDateLabel = selectedDay ? formatFullDate(selectedDay) : null;

  const closeModal = () => setActiveAssignment(null);

  const hasActiveFilters = selectedAssignmentType !== "all";

  const handleResetFilters = () => {
    setSelectedAssignmentType("all");
  };

  return (
    <section className="paper-assignment-calendar">
      <div className="paper-assignment-calendar__header">
        <div>
          <h4 className="paper-assignment-calendar__title">알림장 및 과제 캘린더</h4>
          <p className="paper-assignment-calendar__subtitle">
            날짜를 눌러 해당 일자에 받은 알림장과 과제를 한눈에 살펴보세요.
          </p>
        </div>
        <div className="paper-assignment-calendar__stats">
          <span className="paper-assignment-calendar__stats-number">
            {totalAssignmentsCount}
          </span>
          <span className="paper-assignment-calendar__stats-label">
            누적 등록 건수
          </span>
        </div>
      </div>

      <div className="paper-assignment-calendar__controls">
        <div className="paper-assignment-calendar__filters">
          <label className="paper-assignment-calendar__filter">
            <span className="paper-assignment-calendar__filter-label">과제 유형</span>
            <select
              value={selectedAssignmentType}
              onChange={(event) => setSelectedAssignmentType(event.target.value)}
              className="paper-assignment-calendar__select"
            >
              <option value="all">전체</option>
              <option value="paper">알림장</option>
              <option value="vocabulary">단어</option>
              <option value="analysis">지문분석</option>
              <option value="comprehensive">내용이해</option>
              <option value="paragraph">모의고사</option>
              <option value="sentence">문장</option>
              <option value="grammar">어법</option>
            </select>
          </label>
        </div>

        <button
          type="button"
          className="paper-assignment-calendar__filter-reset"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
        >
          필터 초기화
        </button>
      </div>

      <div className="paper-assignment-calendar__body">
        <div className="paper-assignment-calendar__picker">
          <div className="custom-calendar">
            <div className="custom-calendar__header">
              <button
                type="button"
                className="custom-calendar__nav-button"
                onClick={() => changeMonth(-1)}
              >
                ‹
              </button>
              <h3 className="custom-calendar__month-label">
                {currentMonth.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
              </h3>
              <button
                type="button"
                className="custom-calendar__nav-button"
                onClick={() => changeMonth(1)}
              >
                ›
              </button>
            </div>

            <div className="custom-calendar__weekdays">
              {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
                <div key={day} className="custom-calendar__weekday">
                  {day}
                </div>
              ))}
            </div>

            <div className="custom-calendar__days">
              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="custom-calendar__day custom-calendar__day--empty" />;
                }

                const dateKey = getDateKey(date);
                const categories = categoriesByDate[dateKey] || [];
                const isSelected = selectedDay && getDateKey(selectedDay) === dateKey;

                return (
                  <div
                    key={dateKey}
                    className={`custom-calendar__day ${isSelected ? 'custom-calendar__day--selected' : ''} ${categories.length > 0 ? 'custom-calendar__day--has-records' : ''}`}
                    onClick={() => handleDayClick(date)}
                  >
                    <div className="custom-day-content">
                      <div className="day-number">{date.getDate()}</div>
                      {categories.length > 0 && (
                        <div className="day-banners">
                          {categories.map((category) => (
                            <span key={category} className={`day-banner day-banner--${category}`}>
                              {getCategoryLabel(category)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="paper-assignment-calendar__hint">
            날짜를 클릭하면 해당일의 알림장과 과제를 확인할 수 있습니다.
          </div>
        </div>

        <div className="paper-assignment-calendar__records">
          {selectedDay ? (
            <>
              <div className="paper-assignment-calendar__selection">
                <div>
                  <p className="paper-assignment-calendar__selection-title">
                    {selectedDateLabel}
                  </p>
                  <p className="paper-assignment-calendar__selection-subtitle">
                    선택한 날짜에 등록된 알림장과 과제를 확인하세요.
                  </p>
                </div>
                <span className="paper-assignment-calendar__count-badge">
                  {selectedAssignments.length}건
                </span>
              </div>

              {selectedAssignments.length === 0 ? (
                <div className="paper-assignment-calendar__empty">
                  이 날짜에는 등록된 알림장이나 과제가 없습니다.
                </div>
              ) : (
                <ul className="paper-assignment-calendar__list">
                  {selectedAssignments.map((assignment) => {
                    // 온라인 과제인 경우
                    if (assignment._recordType === 'online') {
                      const worksheetType = assignment.type;
                      const worksheetTitle = assignment.worksheet?.title || assignment.title;
                      const isFinished = assignment.is_finished || false;
                      // const progressText = isFinished ? "완료" : "진행중";
                      const progressText = "완료";

                      return (
                        <li
                          key={`online-${assignment.id}`}
                          className="paper-assignment-calendar__card"
                          style={{ backgroundColor: '#f0fff4' }}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveAssignment(assignment)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setActiveAssignment(assignment);
                            }
                          }}
                        >
                          <div className="paper-assignment-calendar__card-header paper-assignment-calendar__card-header--student">
                            <span className="paper-assignment-calendar__timestamp">
                              완료일: {formatDateTime(assignment.finished_at || assignment.created_at)}
                            </span>
                            <div className="paper-assignment-calendar__card-chips">
                              {worksheetType && (
                                <span className={`day-banner day-banner--${worksheetType}`}>
                                  {getCategoryLabel(worksheetType)}
                                </span>
                              )}
                              <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                                온라인 과제
                              </span>
                              {/* <span className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${isFinished ? 'positive' : 'warning'}`}> */}
                              <span className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--positive`}>
                                {progressText}
                              </span>
                            </div>
                          </div>

                          {worksheetTitle ? (
                            <p className="paper-assignment-calendar__card-comment" title={worksheetTitle} style={{ fontWeight: 'bold' }}>
                              📝 {worksheetTitle}
                            </p>
                          ) : null}

                          <div className="paper-assignment-calendar__card-footer paper-assignment-calendar__card-footer--student">
                            <button
                              type="button"
                              className="paper-assignment-calendar__card-button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setActiveAssignment(assignment);
                              }}
                            >
                              상세보기
                            </button>
                          </div>
                        </li>
                      );
                    }

                    // 알림장 (paper assignment)인 경우
                    const classContentItems = parseClassContent(
                      assignment.class_content
                    );
                    const classContentPreview = classContentItems.slice(0, 2);
                    const commentPreview = assignment.comment?.trim();
                    const homeworkCount = assignment.homework_files?.length ?? 0;
                    const categories = detectDayCategories(assignment);

                    // class_content에서 파일이 있는 항목 수 계산 (파싱 전 원본 데이터로 확인)
                    let allClassContent = assignment.class_content;
                    if (typeof allClassContent === 'string') {
                      try {
                        allClassContent = JSON.parse(allClassContent);
                      } catch (e) {
                        allClassContent = [];
                      }
                    }
                    const classContentFileCount = Array.isArray(allClassContent)
                      ? allClassContent.filter(
                          (item) => item?.file_url || item?.file_name || item?.s3_key
                        ).length
                      : 0;
                    const totalFileCount = homeworkCount + classContentFileCount;

                    return (
                      <li
                        key={assignment.id}
                        className="paper-assignment-calendar__card"
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveAssignment(assignment)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setActiveAssignment(assignment);
                          }
                        }}
                      >
                        <div className="paper-assignment-calendar__card-header paper-assignment-calendar__card-header--student">
                          <span className="paper-assignment-calendar__timestamp">
                            {formatDateTime(assignment.class_date || assignment.created_at)}
                          </span>
                          <div className="paper-assignment-calendar__card-chips">
                            <span
                              className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                                !assignment.attendance && assignment.attendance !== false ? "neutral" : assignment.attendance ? "positive" : "negative"
                              }`}
                            >
                              {!assignment.attendance && assignment.attendance !== false ? "출결 -" : assignment.attendance ? "출석" : "결석"}
                            </span>
                            <span
                              className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                                !assignment.attitude
                                  ? "neutral"
                                  : assignment.attitude === "상"
                                  ? "positive"
                                  : assignment.attitude === "중"
                                  ? "warning"
                                  : "negative"
                              }`}
                            >
                              태도 {assignment.attitude || "-"}
                            </span>
                            {categories.map((category) => (
                              <span key={category} className={`day-banner day-banner--${category}`}>
                                {getCategoryLabel(category)}
                              </span>
                            ))}
                          </div>
                        </div>

                        {commentPreview ? (
                          <p className="paper-assignment-calendar__card-comment" title={commentPreview}>
                            💬 {commentPreview}
                          </p>
                        ) : null}

                        {classContentItems.length > 0 ? (
                          <div className="paper-assignment-calendar__card-content">
                            {classContentPreview.map((item, index) => (
                              <span key={`${item.type}-${index}`}>
                                {item.description}
                              </span>
                            ))}
                            {classContentItems.length > classContentPreview.length ? (
                              <span className="paper-assignment-calendar__card-content-more">
                                외 {classContentItems.length - classContentPreview.length}건
                              </span>
                            ) : null}
                          </div>
                        ) : null}

                        <div className="paper-assignment-calendar__card-footer paper-assignment-calendar__card-footer--student">
                          {totalFileCount > 0 && (
                            <span className="paper-assignment-calendar__card-meta">
                              📎 과제 파일 {totalFileCount}개
                            </span>
                          )}
                          <button
                            type="button"
                            className="paper-assignment-calendar__card-button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActiveAssignment(assignment);
                            }}
                          >
                            상세보기
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : (
            <div className="paper-assignment-calendar__placeholder">
              날짜를 선택하면 알림장 및 과제 기록이 여기에서 보여집니다.
            </div>
          )}
        </div>
      </div>

      {activeAssignment ? (
        <div
          className="paper-assignment-calendar__modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="paper-assignment-calendar__modal"
            role="dialog"
            aria-modal="true"
            aria-label="알림장 및 과제 상세"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="paper-assignment-calendar__modal-close"
              onClick={closeModal}
            >
              ✕
            </button>

            <header className="paper-assignment-calendar__modal-header">
              <div>
                <h3 className="paper-assignment-calendar__modal-title">
                  {activeAssignment._recordType === 'online' ? '온라인 과제 상세' : '알림장 상세'}
                </h3>
                <p className="paper-assignment-calendar__modal-subtitle">
                  {activeAssignment._recordType === 'online'
                    ? `등록일 ${formatDateTime(activeAssignment.created_at)}`
                    : `수업일 ${formatDateTime(activeAssignment.class_date || activeAssignment.created_at)}`}
                </p>
              </div>
              <div className="paper-assignment-calendar__modal-chips">
                {activeAssignment._recordType === 'online' ? (
                  <>
                    {activeAssignment.type && (
                      <span className={`day-banner day-banner--${activeAssignment.type}`}>
                        {getCategoryLabel(activeAssignment.type)}
                      </span>
                    )}
                    <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                      온라인 과제
                    </span>
                  </>
                ) : (
                  <>
                    <span
                      className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                        !activeAssignment.attendance && activeAssignment.attendance !== false ? "neutral" : activeAssignment.attendance ? "positive" : "negative"
                      }`}
                    >
                      {!activeAssignment.attendance && activeAssignment.attendance !== false ? "출결 -" : activeAssignment.attendance ? "출석" : "결석"}
                    </span>
                    <span
                      className={`paper-assignment-calendar__chip paper-assignment-calendar__chip--${
                        !activeAssignment.attitude
                          ? "neutral"
                          : activeAssignment.attitude === "상"
                          ? "positive"
                          : activeAssignment.attitude === "중"
                          ? "warning"
                          : "negative"
                      }`}
                    >
                      태도 {activeAssignment.attitude || "-"}
                    </span>
                    {detectDayCategories(activeAssignment).map((category) => (
                      <span key={category} className={`day-banner day-banner--${category}`}>
                        {getCategoryLabel(category)}
                      </span>
                    ))}
                  </>
                )}
              </div>
            </header>

            {activeAssignment._recordType === 'online' ? (
              <>
                <section className="paper-assignment-calendar__modal-section">
                  <h4 className="paper-assignment-calendar__section-title">과제 정보</h4>
                  <p className="paper-assignment-calendar__section-content">
                    <strong>제목:</strong> {activeAssignment.worksheet?.title || activeAssignment.title || "제목 없음"}
                  </p>
                  <p className="paper-assignment-calendar__section-content">
                    <strong>유형:</strong> {activeAssignment.type ? getCategoryLabel(activeAssignment.type) : "유형 없음"}
                  </p>
                  <p className="paper-assignment-calendar__section-content">
                    {/* <strong>상태:</strong> {activeAssignment.is_finished ? "완료" : "진행중"} */}
                    <strong>상태: 완료</strong>
                  </p>
                </section>

                {activeAssignment.commentary_pdf_path && activeAssignment.commentary_pdf_path !== "" && (
                  <section className="paper-assignment-calendar__modal-section">
                    <h4 className="paper-assignment-calendar__section-title">해설지</h4>
                    <button
                      type="button"
                      onClick={() => showCommentaryPDF(activeAssignment.id, student.id)}
                      style={{
                        display: 'inline-block',
                        padding: '10px 20px',
                        backgroundColor: '#11346A',
                        color: '#FFF',
                        border: 'none',
                        borderRadius: '6px',
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        textAlign: 'left'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0F2A5A'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#11346A'}
                    >
                      📄 해설지 보기
                    </button>
                  </section>
                )}

                {activeAssignment.responses && activeAssignment.responses.length > 0 ? (
                  <section className="paper-assignment-calendar__modal-section">
                    <h4 className="paper-assignment-calendar__section-title">
                      나의 답변 ({activeAssignment.responses.length}개)
                    </h4>
                    <ul className="paper-assignment-calendar__content-list">
                      {activeAssignment.responses.map((response, index) => (
                        <li key={response.id || index}>
                          <span className="paper-assignment-calendar__content-badge">
                            문제 {index + 1}
                          </span>
                          <span>
                            {response.submitted ? "제출 완료" : "미제출"}
                            {response.score !== null && response.score !== undefined ? ` (${response.score}점)` : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}
              </>
            ) : null}

            {activeAssignment.report_link ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">알림장</h4>
                  <a
                    href={`${secret.API_BASE_URL}/paper_report/${activeAssignment.report_link.token}/`.replace(/([^:]\/)\/+/g, '$1')}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      backgroundColor: '#11346A',
                      color: '#FFF',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#0F2A5A'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#11346A'}
                  >
                    📄 알림장 보기
                  </a>
              </section>
            ) : null}

            {activeAssignment.comment ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">코멘트</h4>
                <p className="paper-assignment-calendar__section-content">
                  {activeAssignment.comment}
                </p>
              </section>
            ) : null}

            {modalClassContent.length > 0 ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">수업 내용</h4>
                <ul className="paper-assignment-calendar__content-list">
                  {modalClassContent.map((item, index) => {
                    const hasFile = item.file_url || item.file_name || item.s3_key;

                    return (
                      <li key={`${item.type}-${index}`}>
                        <span className="paper-assignment-calendar__content-badge">
                          {getContentLabel(item.type)}
                        </span>
                        <span>{item.description}</span>
                        {hasFile && (
                          <a
                            href={item.s3_key ? `https://pub-773bb0abd56d4402937254e0cdd4fd1c.r2.dev/${item.s3_key}` : item.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="paper-assignment-calendar__content-file-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            📎 {item.file_name || '파일 보기'}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {Array.isArray(activeAssignment.homework_files) &&
            activeAssignment.homework_files.length > 0 ? (
              <section className="paper-assignment-calendar__modal-section">
                <h4 className="paper-assignment-calendar__section-title">
                  과제 파일 ({activeAssignment.homework_files.length}개)
                </h4>
                <ul className="paper-assignment-calendar__file-list">
                  {activeAssignment.homework_files.map((file) => {
                    // 과제 파일이 있는지 확인
                    const hasOriginalFile = file.s3_key && file.file_name;
                    // 학생 제출 파일이 있는지 확인
                    const hasSubmission = file.student_submission?.submission_s3_key;

                    return (
                      <li key={file.id}>
                        <div>
                          <p className="paper-assignment-calendar__file-title">
                            {file.title}
                          </p>
                          {file.file_name && (
                            <p className="paper-assignment-calendar__file-name">
                              {file.file_name}
                            </p>
                          )}
                          {file.student_submission ? (
                            <p className="paper-assignment-calendar__file-submission">
                              {file.student_submission.score !== null &&
                              file.student_submission.score !== undefined ? (
                                <span className="paper-assignment-calendar__chip paper-assignment-calendar__chip--positive">
                                  점수 {file.student_submission.score}점
                                </span>
                              ) : null}
                              <span className="paper-assignment-calendar__submission-status">
                                제출 {file.student_submission.submission_file_name ? "완료" : "대기"}
                              </span>
                            </p>
                          ) : null}

                          {/* END DATE Display */}
                          {file.end_date && (
                            <div style={{
                              marginTop: '8px',
                              padding: '8px 12px',
                              backgroundColor: isHomeworkOverdue(file.end_date) ? '#fff5f5' : '#f8f9fa',
                              border: `1px solid ${isHomeworkOverdue(file.end_date) ? '#feb2b2' : '#e2e8f0'}`,
                              borderRadius: '5px',
                              fontSize: '14px',
                              color: isHomeworkOverdue(file.end_date) ? '#c53030' : '#4a5568'
                            }}>
                              <span style={{ fontWeight: '500' }}>제출 마감일: </span>
                              {formatDeadline(file.end_date)}
                              {isHomeworkOverdue(file.end_date) && (
                                <span style={{ marginLeft: '8px', fontWeight: '600', color: '#c53030' }}>
                                  (마감됨)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {hasOriginalFile && (
                            <button
                              type="button"
                              className="paper-assignment-calendar__download-button"
                              onClick={() => onDownloadHomework(file.s3_key, file.file_name)}
                            >
                              과제 파일 다운로드
                            </button>
                          )}
                          {hasSubmission && (
                            <button
                              type="button"
                              className="paper-assignment-calendar__download-button"
                              onClick={() => onDownloadHomework(file.student_submission.submission_s3_key, file.student_submission.submission_file_name)}
                            >
                              내 제출 파일 다운로드
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default StudentPaperAssignmentCalendar;
